//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState } from 'react';
import { ListGroup, Button } from 'react-bootstrap';
import CustomModal from './CustomModal';

// Context
import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUser } from '../../../../../API/Context/UserContext';
import { useActivePatient } from '../../../../../API/Hooks/ActivePatientContext';
import { usePatient } from '../../../../../API/Hooks/PatientContext';

// Config
import { MARITAL_STATUS } from '../../../../../ConfigFiles/maps/marital_status_map';
import { EDUCATION_CONFIG } from '../../../../../ConfigFiles/maps/education_map';
import { RELIGION_CONFIG } from '../../../../../ConfigFiles/maps/religion_map';

import VocabGeneralDisplay from '../../../../../ConfigFiles/multilingual/VocabGeneralDisplay';
import VocabSexes from '../../../../../ConfigFiles/multilingual/VocabSexes';
import { VocabButtons as btn_vocab } from '../../../../../ConfigFiles/multilingual/VocabButtons';

// Utils
import { transformedDate } from '../../../../../Utils/dateUtils';
import { getEnumLabel } from '../../../../../Utils/HelperFunctions/enumUtils';

// Types
import type { PartialGeneralInfoUpdate } from '../../../../../Types/schema-helpers';

//  ----------      END   IMPORT  BLOCK       ----------

const GeneralDisplay: React.FC = () => {
  // Context
  const { lan } = useLanguage();
  const { isProfessional } = useUser();
  const { activePatient } = useActivePatient();
  const { updatePatientProfile } = usePatient();

  const [showModal, setShowModal] = useState<boolean>(false);

  // Extract patient record from activePatient
  const patient = activePatient?.patient;

  // Handle loading/missing state before rendering data
  if (!patient) {
    return <div>{VocabGeneralDisplay.loading_data[lan]}</div>;
  }

  // Enable/disable edit permission if user is a professional
  const isEditable = isProfessional;

  // Handle saving changes via modal using patient context
  const handleSave = async (receivedData: PartialGeneralInfoUpdate) => {
    if (!patient.username) {
      console.error("Patient username is missing.");
      return;
    }

    const isSuccess = await updatePatientProfile(patient.username, receivedData);
    if (isSuccess) {
      setShowModal(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <div className="w-100">
      {isEditable && (
        <div className="d-flex justify-content-end mb-2">
          <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
            {btn_vocab.update[lan]}
          </Button>
        </div>
      )}

      <ListGroup variant="flush">
        <ListGroup.Item>
          {VocabGeneralDisplay.birthday[lan]}: {patient.birthday ? transformedDate(patient.birthday) : '—'}
        </ListGroup.Item>
        <ListGroup.Item>
          {VocabGeneralDisplay.birthplace[lan]}: {patient.birthplace || '—'}
        </ListGroup.Item>

        <ListGroup.Item>
          {VocabGeneralDisplay.biological_sex[lan]}: {Number(patient.biological_sex) === 0 ? VocabSexes.female[lan] : VocabSexes.male[lan]}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.sex[lan]}: {Number(patient.current_sex) === 0 ? VocabSexes.female[lan] : VocabSexes.male[lan]}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.height[lan]}: {patient.height ?? '—'}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.weight[lan]}: {patient.weight ?? '—'}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.marital_status[lan]}: {getEnumLabel(MARITAL_STATUS, patient.marital_status, lan)}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.education[lan]}: {getEnumLabel(EDUCATION_CONFIG, patient.education, lan)}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.occupation[lan]}: {patient.occupation || '—'}
        </ListGroup.Item>

        <ListGroup.Item className="d-flex justify-content-between align-items-center">
          {VocabGeneralDisplay.religion[lan]}: {getEnumLabel(RELIGION_CONFIG, patient.religion, lan)}
        </ListGroup.Item>
      </ListGroup>

      {showModal && (
        <CustomModal
          onSave={handleSave}
          onHide={handleModalClose}
          initialData={patient}
        />
      )}
    </div>
  );
};

export default GeneralDisplay;